'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion'
import type { Variants } from 'framer-motion'
import { Star, Sparkles, Filter as FilterIcon, ChevronDown, BadgeCheck } from 'lucide-react'
import { CountUp } from '@/components/ui/count-up'
import { EASE, SPRING, VIEWPORT } from '@/lib/motion'

// Motion Wave D set-piece for /reviews: the hero PDP review-card mock,
// extracted verbatim from the page so the server component stays a server
// component. When the card scrolls into view it settles up once, the 4.9
// counts up, the five stars pop in sequence on a spring, and the photo
// strip / review / attribute sliders fade up in a short stagger. Plays
// once; reduced motion renders the final state instantly.

const cardIn: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE, staggerChildren: 0.07, delayChildren: 0.2 },
  },
}

const fadeItem: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
}

const starRow: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
}

const starPop: Variants = {
  hidden: { opacity: 0, scale: 0.5 },
  show: { opacity: 1, scale: 1, transition: SPRING },
}

export function ReviewsHeroCard() {
  const reduce = useReducedMotion()

  return (
    <LazyMotion features={domAnimation}>
      <div className="relative">
        <span className="absolute -left-4 -top-4 z-10 inline-flex rounded-full bg-amber-300 px-2.5 py-1 font-display text-[11px] font-semibold text-amber-950 shadow-sm">
          Product reviews
        </span>
        <m.div
          className="rounded-3xl border bg-card p-5 shadow-xl md:p-6"
          initial={reduce ? false : 'hidden'}
          whileInView={reduce ? undefined : 'show'}
          viewport={VIEWPORT}
          variants={cardIn}
        >
          {/* Product image */}
          <div className="relative aspect-[5/4] overflow-hidden rounded-2xl bg-gradient-to-br from-amber-100 via-rose-100 to-amber-200">
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="h-10 w-10 text-amber-600/60" />
            </div>
          </div>
          <button className="mt-4 w-full rounded-xl bg-foreground py-2.5 text-sm font-semibold text-background">Buy now</button>
          <m.div
            className="mt-4 flex items-center gap-2"
            initial={reduce ? false : undefined}
            variants={reduce ? undefined : fadeItem}
          >
            <p className="font-display text-2xl font-semibold"><CountUp value={4.9} decimals={1} /></p>
            <m.div className="flex" initial={reduce ? false : undefined} variants={reduce ? undefined : starRow}>
              {Array.from({ length: 5 }).map((_, i) => (
                <m.span key={i} className="flex" initial={reduce ? false : undefined} variants={reduce ? undefined : starPop}>
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                </m.span>
              ))}
            </m.div>
          </m.div>
          <p className="-mt-0.5 text-xs text-muted-foreground">Based on 3,561 reviews</p>

          {/* Tiny photo strip */}
          <m.div
            className="mt-3 flex gap-1.5"
            initial={reduce ? false : undefined}
            variants={reduce ? undefined : fadeItem}
          >
            {['rose', 'amber', 'emerald', 'sky', 'teal'].map((c) => (
              <div key={c} className={`h-9 w-9 shrink-0 rounded-md bg-gradient-to-br ${
                c === 'rose' ? 'from-rose-300 to-rose-100'
                : c === 'amber' ? 'from-amber-300 to-amber-100'
                : c === 'emerald' ? 'from-emerald-300 to-emerald-100'
                : c === 'sky' ? 'from-sky-300 to-sky-100'
                : 'from-teal-300 to-teal-100'}`} />
            ))}
            <span className="ml-auto self-center text-[11px] text-muted-foreground">+312 photos</span>
          </m.div>

          {/* Tabs */}
          <div className="mt-4 flex items-center justify-between border-b text-xs">
            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-1 py-2 text-muted-foreground"><FilterIcon className="h-3 w-3" />Filter <ChevronDown className="h-3 w-3" /></button>
            </div>
            <div className="flex items-center gap-4">
              <span className="border-b-2 border-foreground pb-2 font-semibold">Reviews</span>
              <span className="pb-2 text-muted-foreground">Question</span>
            </div>
          </div>

          {/* Single review */}
          <m.div
            className="mt-3 flex items-start gap-2.5"
            initial={reduce ? false : undefined}
            variants={reduce ? undefined : fadeItem}
          >
            <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-rose-200 to-amber-200" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold">Jennie K.</p>
                <m.div className="flex" initial={reduce ? false : undefined} variants={reduce ? undefined : starRow}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <m.span key={i} className="flex" initial={reduce ? false : undefined} variants={reduce ? undefined : starPop}>
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                    </m.span>
                  ))}
                </m.div>
              </div>
              <p className="inline-flex items-center gap-1 text-[10px] text-emerald-600">
                <BadgeCheck className="h-3 w-3" />Verified Customer
              </p>
            </div>
          </m.div>

          {/* Attribute sliders */}
          <m.div
            className="mt-3 grid grid-cols-2 gap-3"
            initial={reduce ? false : undefined}
            variants={reduce ? undefined : fadeItem}
          >
            {['Results', 'Value for money'].map((l) => (
              <div key={l}>
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{l}</p>
                <div className="mt-1 h-1 rounded-full bg-secondary">
                  <div className="h-1 w-4/5 rounded-full bg-foreground" />
                </div>
                <div className="mt-0.5 flex justify-between text-[9px] text-muted-foreground">
                  <span>Poor</span><span>Excellent</span>
                </div>
              </div>
            ))}
          </m.div>
        </m.div>
      </div>
    </LazyMotion>
  )
}
